import { useState, useEffect, useRef } from 'react';
import { _T, C } from '../../shared/theme';
import { s_PB, s_GB, s_TI } from '../../shared/styles';
import { saveBannerImage } from '../../shared/dbService';
import AvatarUploader from './AvatarUploader';

const PRESETS = [
  { label:"Midnight", bg:"#080e1a", accent:"#C8A84B" },
  { label:"Ocean",    bg:"#041220", accent:"#2ABFCF" },
  { label:"Cosmos",   bg:"#0e0820", accent:"#A99EE8" },
  { label:"Forest",   bg:"#061209", accent:"#4AC87A" },
  { label:"Ember",    bg:"#1a0800", accent:"#D4873A" },
  { label:"Rose",     bg:"#1a0810", accent:"#E07090" },
];

function HeroBannerEditor({ name, bannerColor, setBannerColor, accentColor, setAccentColor, intention, lang, edit, setEdit, tmp, setTmp, setName, userId }) {
  const nl = lang === "nl";
  const [editing, setEditing] = useState(false);
  const [bannerImg, setBannerImg] = useState(null);
  const bannerFileRef = useRef(null);
  const storageKey = 'sl_banner_img';

  useEffect(() => {
    try {
      const b = localStorage.getItem(storageKey);
      if (b) setBannerImg(b);
    } catch {}
  }, [storageKey]);

  const handleBannerUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const base64 = ev.target.result;
      setBannerImg(base64);
      try { localStorage.setItem(storageKey, base64); } catch {}
      saveBannerImage(base64);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const removeBannerImg = () => {
    setBannerImg(null);
    try { localStorage.removeItem(storageKey); } catch {}
    saveBannerImage(null);
  };

  return (
    <div style={{ marginBottom: 14, borderRadius: 20, overflow: "hidden", border: `1px solid ${accentColor}33` }}>

      {/* ── Banner area ── */}
      <div style={{ position: "relative", height: 120, overflow: "hidden" }}>
        {/* Background: photo if available, else color gradient */}
        {bannerImg
          ? <img src={bannerImg} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}/>
          : <div style={{ position: "absolute", inset: 0, background: `linear-gradient(135deg,${bannerColor},${accentColor}22)` }}/>
        }
        {/* Gradient overlay — always on top of photo */}
        <div style={{ position: "absolute", inset: 0, background: `linear-gradient(135deg,${bannerColor}cc,${accentColor}44)` }}/>
        <div style={{ position: "absolute", inset: 0, background: `radial-gradient(ellipse at 75% 40%,${accentColor}28,transparent 65%)` }}/>

        <button
          onClick={() => setEditing(e => !e)}
          style={{ position: "absolute", top: 10, right: 10, background: "rgba(0,0,0,0.5)", border: `1px solid ${accentColor}55`, color: accentColor, borderRadius: 8, padding: "5px 10px", cursor: "pointer", fontSize: 11, fontWeight: 600 }}
        >
          ✏️ {nl ? "Bewerk" : "Edit"}
        </button>
        {intention && (
          <div style={{ position: "absolute", bottom: 10, left: 16, right: 80, fontSize: 11, color: `${accentColor}cc`, fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            "{intention}"
          </div>
        )}
      </div>

      {/* ── Avatar + name row ── */}
      <div style={{ background: _T.bg3, padding: "0 16px 14px" }}>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
          <div style={{ marginTop: -28, flexShrink: 0 }}>
            <AvatarUploader userId={userId} userName={name} accentColor={accentColor} size={56} onImageChange={()=>{}} />
          </div>
          <div style={{ flex: 1, paddingLeft: 12, paddingTop: 10 }}>
            {edit ? (
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input value={tmp} onChange={e => setTmp(e.target.value)} style={s_TI({ flex: 1, padding: "6px 10px", fontSize: 13 })} />
                <button style={s_PB({ padding: "6px 12px", fontSize: 12 })} onClick={() => { setName(tmp); setEdit(false); }}>✓</button>
                <button style={s_GB({ padding: "6px 10px", fontSize: 12 })} onClick={() => setEdit(false)}>✕</button>
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ color: C.white, fontSize: 16, fontWeight: 600 }}>{name}</div>
                <button style={s_GB({ fontSize: 13, padding: "3px 9px", borderRadius: 8 })} onClick={() => { setEdit(true); setTmp(name); }}>✏️</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Edit panel ── */}
      {editing && (
        <div style={{ background: _T.bg4, borderTop: `1px solid ${_T.cardBorderBot}`, padding: 14 }}>

          {/* Banner photo upload */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: C.silver, marginBottom: 8 }}>{nl ? "Bannerfoto" : "Banner photo"}</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => bannerFileRef.current?.click()}
                style={{ ...s_GB({ fontSize: 12, padding: "8px 14px", borderRadius: 10 }), flex: 1 }}
              >
                📷 {nl ? "Foto uploaden" : "Upload photo"}
              </button>
              {bannerImg && (
                <button
                  onClick={removeBannerImg}
                  style={{ background: "transparent", border: `1px solid ${C.red}44`, color: C.red, borderRadius: 10, padding: "8px 12px", cursor: "pointer", fontSize: 12 }}
                >
                  🗑
                </button>
              )}
            </div>
            <input ref={bannerFileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleBannerUpload} />
            {bannerImg && (
              <div style={{ marginTop: 8, borderRadius: 10, overflow: "hidden", height: 60, position: "relative" }}>
                <img src={bannerImg} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                <div style={{ position: "absolute", inset: 0, background: `linear-gradient(135deg,${bannerColor}99,${accentColor}33)` }} />
                <div style={{ position: "absolute", bottom: 4, left: 8, fontSize: 10, color: C.silver2 }}>
                  {nl ? "Voorbeeld met kleuroverlay" : "Preview with colour overlay"}
                </div>
              </div>
            )}
          </div>

          {/* Color presets */}
          <div style={{ fontSize: 11, color: C.silver, marginBottom: 8 }}>{nl ? "Kleurthema" : "Colour theme"}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 6, marginBottom: 12 }}>
            {PRESETS.map(p => {
              const isA = p.bg === bannerColor && p.accent === accentColor;
              return (
                <button key={p.label} onClick={() => { setBannerColor(p.bg); setAccentColor(p.accent); }}
                  style={{ background: p.bg, border: `2px solid ${isA ? p.accent : p.accent + "33"}`, borderRadius: 10, padding: "8px 4px", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                  <div style={{ width: 18, height: 18, borderRadius: "50%", background: p.accent }} />
                  <span style={{ fontSize: 9, color: isA ? p.accent : C.silver, fontWeight: isA ? 700 : 400 }}>{p.label}</span>
                </button>
              );
            })}
          </div>

          <button onClick={() => setEditing(false)} style={s_PB({ width: "100%", padding: "9px", fontSize: 13 })}>
            ✓ {nl ? "Opslaan" : "Save"}
          </button>
        </div>
      )}
    </div>
  );
}

export default HeroBannerEditor;
